import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
    ShoppingCart, Check, ArrowLeft, ArrowRight, Loader2, Package,
    ShieldCheck, Truck, Zap, Info,
} from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { useCart, eur } from "@/context/CartContext";
import useSeo from "@/lib/useSeo";
import { toast } from "sonner";

export default function Produto() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { add, setOpen: setCartOpen } = useCart();
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [related, setRelated] = useState([]);
    const [qty, setQty] = useState(1);
    const [added, setAdded] = useState(false);

    useSeo({
        title: product ? `${product.name} · Loja NOVIFOOD` : "Produto · Loja NOVIFOOD",
        description: product?.description || "Detalhes do produto NOVIFOOD.",
        path: `/loja/${slug}`,
    });

    useEffect(() => {
        let alive = true;
        setLoading(true);
        setNotFound(false);
        (async () => {
            try {
                const { data } = await api.get(`/products/${encodeURIComponent(slug)}`);
                if (!alive) return;
                setProduct(data);
                // load related (same category, excluding this)
                try {
                    const { data: allProducts } = await api.get(`/products?category=${data.category}`);
                    if (alive) {
                        setRelated(allProducts.filter(p => p.id !== data.id).slice(0, 3));
                    }
                } catch { /* ignore */ }
            } catch (e) {
                if (!alive) return;
                if (e?.response?.status === 404) setNotFound(true);
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [slug]);

    const handleAdd = (redirect = false) => {
        if (!product) return;
        add(product, qty);
        if (redirect) {
            setCartOpen(false);
            navigate("/checkout");
        } else {
            setAdded(true);
            setTimeout(() => setAdded(false), 1500);
        }
    };

    if (loading) {
        return (
            <div className="container-x py-24 flex justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    if (notFound || !product) {
        return (
            <section className="container-x py-24 text-center max-w-xl">
                <Package className="h-12 w-12 text-secondary mx-auto" />
                <h1 className="mt-5 font-heading text-3xl font-extrabold text-primary">
                    Produto não encontrado
                </h1>
                <p className="mt-3 text-muted-foreground">
                    Não conseguimos encontrar o produto <span className="font-mono text-primary">{slug}</span>.
                </p>
                <Link
                    to="/loja"
                    className="mt-8 inline-flex items-center gap-2 rounded-full bg-secondary px-5 py-2.5 text-sm font-bold text-secondary-foreground"
                >
                    <ArrowLeft className="h-4 w-4" /> Voltar à loja
                </Link>
            </section>
        );
    }

    const stockLow = product.stock != null && product.stock > 0 && product.stock <= 5;
    const outOfStock = product.stock != null && product.stock <= 0;
    const isModel = product.category === "model";
    const categoryLabel = isModel ? "Modelo" : "Acessório";

    return (
        <>
            {/* Breadcrumb */}
            <nav className="container-x pt-6" data-testid="produto-breadcrumb">
                <div className="text-xs text-muted-foreground flex items-center gap-2">
                    <Link to="/" className="hover:text-primary">Home</Link>
                    <span aria-hidden>/</span>
                    <Link to="/loja" className="hover:text-primary">Loja</Link>
                    <span aria-hidden>/</span>
                    <span className="text-primary font-bold">{product.name}</span>
                </div>
            </nav>

            {/* Main */}
            <section className="container-x py-8 lg:py-12" data-testid="produto-page">
                <div className="grid gap-10 lg:grid-cols-2 lg:gap-14">
                    {/* Left — gallery */}
                    <div>
                        <div className="aspect-[4/3] rounded-2xl bg-muted overflow-hidden border border-border">
                            {product.image_url ? (
                                <img
                                    src={mediaUrl(product.image_url)}
                                    alt={product.name}
                                    className="h-full w-full object-cover"
                                />
                            ) : (
                                <div className="h-full w-full flex items-center justify-center">
                                    <Package className="h-20 w-20 text-muted-foreground" />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Right — details */}
                    <div>
                        <span className="inline-flex items-center gap-1 rounded-full bg-secondary/15 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-primary">
                            {categoryLabel}
                        </span>
                        <h1 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-primary leading-tight">
                            {product.name}
                        </h1>

                        <div className="mt-6 flex items-baseline gap-3 flex-wrap">
                            <span className="font-heading text-4xl font-extrabold text-primary" data-testid="produto-price">
                                {eur(product.price_cents)}
                            </span>
                            <span className="text-sm text-muted-foreground">
                                <span className="font-bold uppercase tracking-wider text-xs">s/IVA</span>
                                <span className="ml-1 text-xs">· IVA (23%) adicionado no checkout</span>
                            </span>
                        </div>

                        {/* Stock badge */}
                        <div className="mt-4 flex items-center gap-3 text-sm">
                            {outOfStock ? (
                                <span className="inline-flex items-center gap-1.5 rounded-full bg-red-50 border border-red-200 px-3 py-1 font-bold text-red-700 text-xs">
                                    <span className="inline-block h-2 w-2 rounded-full bg-red-500" /> Esgotado
                                </span>
                            ) : stockLow ? (
                                <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 border border-amber-200 px-3 py-1 font-bold text-amber-700 text-xs">
                                    <span className="inline-block h-2 w-2 rounded-full bg-amber-500" /> Últimas {product.stock} unidades
                                </span>
                            ) : (
                                <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 px-3 py-1 font-bold text-emerald-700 text-xs">
                                    <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" /> Em stock
                                </span>
                            )}
                        </div>

                        {product.description && (
                            <p className="mt-6 text-base text-muted-foreground leading-relaxed">
                                {product.description}
                            </p>
                        )}

                        {/* Quantity + CTAs */}
                        {!outOfStock && (
                            <div className="mt-8 space-y-4">
                                <div>
                                    <label className="text-xs font-bold uppercase tracking-[0.16em] text-primary/60">
                                        Quantidade
                                    </label>
                                    <div className="mt-2 inline-flex items-center gap-2 rounded-lg border border-border overflow-hidden">
                                        <button
                                            type="button"
                                            onClick={() => setQty(q => Math.max(1, q - 1))}
                                            data-testid="produto-qty-minus"
                                            className="h-11 w-11 inline-flex items-center justify-center hover:bg-muted text-primary font-bold text-lg"
                                        >
                                            −
                                        </button>
                                        <input
                                            type="number"
                                            min="1"
                                            max={product.stock || 99}
                                            value={qty}
                                            onChange={(e) => {
                                                const v = parseInt(e.target.value, 10) || 1;
                                                setQty(Math.max(1, Math.min(product.stock || 99, v)));
                                            }}
                                            data-testid="produto-qty-input"
                                            className="h-11 w-14 text-center bg-white text-primary font-bold outline-none"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setQty(q => Math.min(product.stock || 99, q + 1))}
                                            data-testid="produto-qty-plus"
                                            className="h-11 w-11 inline-flex items-center justify-center hover:bg-muted text-primary font-bold text-lg"
                                        >
                                            +
                                        </button>
                                    </div>
                                </div>

                                <div className="flex flex-wrap gap-3">
                                    <button
                                        type="button"
                                        onClick={() => handleAdd(false)}
                                        data-testid="produto-add-to-cart"
                                        className="inline-flex items-center gap-2 rounded-full bg-secondary px-6 py-3 text-sm font-bold text-secondary-foreground shadow-[0_8px_24px_-8px_rgba(224,174,50,0.5)] transition hover:-translate-y-0.5 hover:shadow-[0_14px_32px_-10px_rgba(224,174,50,0.6)]"
                                    >
                                        {added ? <Check className="h-4 w-4" /> : <ShoppingCart className="h-4 w-4" />}
                                        {added ? "Adicionado" : "Adicionar ao carrinho"}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleAdd(true)}
                                        data-testid="produto-buy-now"
                                        className="inline-flex items-center gap-2 rounded-full border-2 border-primary bg-white px-6 py-3 text-sm font-bold text-primary transition hover:bg-primary hover:text-white"
                                    >
                                        Comprar já <ArrowRight className="h-4 w-4" />
                                    </button>
                                </div>
                            </div>
                        )}

                        {outOfStock && (
                            <div className="mt-8 rounded-xl bg-muted border border-border p-4 text-sm text-muted-foreground flex gap-2 items-start">
                                <Info className="h-4 w-4 shrink-0 mt-0.5" />
                                <div>
                                    Este produto está temporariamente esgotado. <Link to="/contacto" className="font-bold text-primary hover:underline">Contacte-nos</Link> para saber a próxima data de disponibilidade.
                                </div>
                            </div>
                        )}

                        {/* Trust badges */}
                        <div className="mt-10 grid grid-cols-3 gap-3 border-t border-border pt-6">
                            <TrustBadge icon={ShieldCheck} label="Garantia 24m" />
                            <TrustBadge icon={Truck} label="Entrega 4-6 sem." />
                            <TrustBadge icon={Zap} label="Solar-ready" />
                        </div>
                    </div>
                </div>
            </section>

            {/* Related products */}
            {related.length > 0 && (
                <section className="border-t border-border bg-muted/40">
                    <div className="container-x section-y">
                        <h2 className="font-heading text-2xl font-bold text-primary mb-6">
                            Outros {isModel ? "modelos" : "acessórios"}
                        </h2>
                        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                            {related.map((p) => (
                                <Link
                                    key={p.id}
                                    to={`/loja/${p.slug}`}
                                    data-testid={`related-${p.slug}`}
                                    className="group rounded-2xl border border-border bg-white overflow-hidden flex flex-col transition hover:shadow-xl hover:-translate-y-0.5"
                                >
                                    <div className="aspect-[4/3] bg-muted overflow-hidden">
                                        {p.image_url ? (
                                            <img
                                                src={mediaUrl(p.image_url)}
                                                alt={p.name}
                                                className="h-full w-full object-cover transition group-hover:scale-105"
                                            />
                                        ) : (
                                            <div className="h-full w-full flex items-center justify-center">
                                                <ShoppingCart className="h-8 w-8 text-muted-foreground" />
                                            </div>
                                        )}
                                    </div>
                                    <div className="p-4">
                                        <h3 className="font-heading text-base font-bold text-primary">{p.name}</h3>
                                        <div className="mt-2 flex items-center justify-between">
                                            <div className="flex items-baseline gap-1">
                                                <span className="font-bold text-primary">{eur(p.price_cents)}</span>
                                                <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">s/IVA</span>
                                            </div>
                                            <span className="text-xs font-bold text-primary/50 uppercase tracking-wider">
                                                Ver detalhes →
                                            </span>
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}
        </>
    );
}

function TrustBadge({ icon: Icon, label }) {
    return (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <div className="h-8 w-8 rounded-full bg-primary/5 flex items-center justify-center">
                <Icon className="h-4 w-4 text-primary" />
            </div>
            <span className="font-bold text-primary">{label}</span>
        </div>
    );
}
